import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
} from "@angular/core";

@Directive({
  selector: "[coreUiSimpleTooltip]",
  standalone: false,
})
export class SimpleTooltipDirective implements OnDestroy {
  @Input("coreUiSimpleTooltip") text = "";
  @Input() tooltipFollowMouse = false;
  @Input() simpleTooltipPosition: "top" | "bottom" | "left" | "right" = "top";
  @Input() simpleTooltipOffset = 8;

  private tooltipEl?: HTMLDivElement;

  constructor(private el: ElementRef<HTMLElement>) {}

  @HostListener("mouseenter", ["$event"])
  show(event: MouseEvent): void {
    this.hide();

    if (!this.text || this.isMobile() || this.isDisabled()) return;

    this.tooltipEl = document.createElement("div");
    this.tooltipEl.textContent = this.text;

    Object.assign(this.tooltipEl.style, {
      position: "fixed",
      zIndex: "9999",
      padding: "6px 9px",
      borderRadius: "7px",
      background: "#242424",
      boxShadow: "0 0 0 1px inset #000000",
      color: "#FFFFFF",
      fontSize: "11px",
      fontWeight: "500",
      lineHeight: "1",
      pointerEvents: "none",
      whiteSpace: "nowrap",
      transform: "translate(-50%, -130%)",
      userSelect: "none",
    });

    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();

    const gap = this.simpleTooltipOffset;
    const positions = {
      top: {
        left: rect.left + rect.width / 2,
        top: rect.top + 4,
        transform: "translate(-50%, -130%)",
      },
      bottom: {
        left: rect.left + rect.width / 2,
        top: rect.bottom + gap,
        transform: "translate(-50%, 0)",
      },
      left: {
        left: rect.left - gap,
        top: rect.top + rect.height / 2,
        transform: "translate(-100%, -50%)",
      },
      right: {
        left: rect.right + gap,
        top: rect.top + rect.height / 2,
        transform: "translate(0, -50%)",
      },
    } as const;
    const position = positions[this.simpleTooltipPosition];

    this.tooltipEl.style.left = `${position.left}px`;
    this.tooltipEl.style.top = `${position.top}px`;
    this.tooltipEl.style.transform = position.transform;

    document.body.appendChild(this.tooltipEl);
  }

  @HostListener("mousemove", ["$event"])
  onMouseMove(event: MouseEvent): void {
    if (!this.tooltipFollowMouse) return;

    this.moveTooltip(event);
  }

  @HostListener("mouseleave")
  hide(): void {
    this.tooltipEl?.remove();
    this.tooltipEl = undefined;
  }

  @HostListener("mousedown")
  @HostListener("click")
  hideOnClick(): void {
    this.hide();
  }

  ngOnDestroy(): void {
    this.hide();
  }

  private moveTooltip(event: MouseEvent): void {
    if (!this.tooltipEl) return;

    this.tooltipEl.style.left = `${event.clientX}px`;
    this.tooltipEl.style.top = `${event.clientY - 10}px`;
  }

  private isDisabled(): boolean {
    const element = this.el.nativeElement;

    return (
      element.hasAttribute("disabled") ||
      element.getAttribute("aria-disabled") === "true" ||
      element.classList.contains("disabled")
    );
  }

  private isMobile(): boolean {
    return window.innerWidth <= 768;
  }
}
